import axiosInstance from '@/lib/axios';

export interface ExpenseItem {
  id?: string;
  _id?: string;
  description: string;
  category: string;
  amount: number;
  date: string; // ISO date string
  paymentMethod: string;
  // UI uses "Pending"/"Paid", backend expects "pending"/"approved"/"rejected"
  status: string;
}

export interface ExpenseFilters {
  search?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export interface ExpensesResponse {
  data: ExpenseItem[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

class ExpenseService {
  private mapStatusToBackend(uiStatus: string): string {
    const s = (uiStatus || '').toLowerCase();
    if (s === 'paid' || s === 'approved') return 'approved';
    if (s === 'pending') return 'pending';
    if (s === 'rejected') return 'rejected';
    return s || 'pending';
  }

  async getExpenses(filters: ExpenseFilters = {}): Promise<ExpensesResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const url = `/expenses?${params.toString()}`;
      const response = await axiosInstance.get(url);

      // Normalize different backend response shapes
      const payload = response.data;
      if (payload?.success && Array.isArray(payload.data)) {
        return {
          data: payload.data,
          total: payload.pagination?.total ?? payload.data.length,
          page: payload.pagination?.page ?? 1,
          limit: payload.pagination?.limit ?? payload.data.length,
          hasMore: payload.pagination?.hasNextPage ?? false,
        };
      }
      if (Array.isArray(payload)) {
        return { data: payload, total: payload.length, page: 1, limit: payload.length, hasMore: false };
      }
      return {
        data: payload.data ?? [],
        total: payload.total ?? (payload.data?.length ?? 0),
        page: payload.page ?? 1,
        limit: payload.limit ?? (payload.data?.length ?? 0),
        hasMore: payload.hasMore ?? false,
      };
    } catch (error) {
      // rethrow to be handled by caller
      throw error;
    }
  }

  async createExpense(data: Omit<ExpenseItem, 'id' | '_id'>): Promise<ExpenseItem> {
    const payload = { ...data, status: this.mapStatusToBackend((data as any).status) };
    const response = await axiosInstance.post('/expenses', payload);
    return response.data?.data ?? response.data;
  }

  async updateExpense(id: string, data: Partial<Omit<ExpenseItem, 'id' | '_id'>>): Promise<ExpenseItem> {
    const payload = { ...data } as any;
    if (payload.status !== undefined) {
      payload.status = this.mapStatusToBackend(payload.status);
    }
    const response = await axiosInstance.patch(`/expenses/${id}`, payload);
    return response.data?.data ?? response.data;
  }

  async deleteExpense(id: string): Promise<void> {
    await axiosInstance.delete(`/expenses/${id}`);
  }
}

export const expenseService = new ExpenseService();


